import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DramaCard from "@/components/feature/DramaCard";
import NewsCard from "@/components/feature/NewsCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchAll } from "@/utils/media";

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") ?? "";
  const [input, setInput] = useState(q);

  usePageMeta({
    title: q ? `“${q}” 搜索结果 - 韩剧TV` : "搜索 - 韩剧TV",
    description: `在韩剧TV搜索“${q}”，查找相关韩剧剧集与资讯内容。`,
    keywords: "韩剧搜索,韩剧查找,韩剧资讯搜索",
  });

  const { dramas: dramaResults, news: newsResults } = searchAll(q);
  const total = dramaResults.length + newsResults.length;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <header className="mt-6">
        <h1 className="font-heading text-2xl font-800 text-foreground-950 md:text-3xl">
          搜索结果
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          关键词：<span className="font-600 text-primary-600">{q || "（空）"}</span>
          {q && <> · 共找到 {total} 条结果</>}
        </p>

        <form onSubmit={submit} className="mt-5 flex items-center gap-2 rounded-full border border-background-300/70 bg-background-100 px-4 py-2 md:max-w-lg">
          <i className="ri-search-line text-base text-foreground-500"></i>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="搜索剧集 / 资讯"
            className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm text-background-50 hover:bg-primary-600 cursor-pointer">
            搜索
          </button>
        </form>
      </header>

      {total === 0 ? (
        <div className="mt-12 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 py-20 text-center">
          <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
          <p className="mt-4 text-base font-500 text-foreground-800">
            {q ? "没有找到相关内容" : "输入关键词开始搜索"}
          </p>
          <p className="mt-2 text-sm text-foreground-500">
            试试“爱情”“悬疑”“星月之约”等关键词，或浏览剧集大全。
          </p>
          <div className="mt-6 flex gap-3">
            <Link to="/category/drama" className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600 cursor-pointer">
              浏览剧集大全
            </Link>
            <Link to="/" className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-10 space-y-12">
          {dramaResults.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-700 text-foreground-950">
                <i className="ri-film-line text-primary-500"></i> 相关剧集
                <span className="text-sm font-400 text-foreground-500">（{dramaResults.length}）</span>
              </h2>
              <div className="mt-6 grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4">
                {dramaResults.map((d) => (
                  <DramaCard key={d.id} drama={d} />
                ))}
              </div>
            </section>
          )}

          {newsResults.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-700 text-foreground-950">
                <i className="ri-newspaper-line text-accent-500"></i> 相关资讯
                <span className="text-sm font-400 text-foreground-500">（{newsResults.length}）</span>
              </h2>
              <div className="mt-6 space-y-4">
                {newsResults.map((n) => (
                  <NewsCard key={n.id} item={n} variant="list" />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}